// general utilities

export const getQueryParameterByName = (name: string, url = window.location.href) => {
    name = name.replace(/[\[\]]/g, '\\$&');
    var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
        results = regex.exec(url);
    if (!results) return '';
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

export const preparePassengersLabel = (options: any) => {
    const adultsNum = parseInt(options.adults, 10) || 0;
    const adultsLabel = `${adultsNum} ${adultsNum === 1 ? options.adultsSingularLabel : options.adultsPluralLabel}`;
    const childrenLabel = prepareLabel(parseInt(options.children, 10), options.childrenSingularLabel, options.childrenPluralLabel);
    const infantsLabel = prepareLabel(parseInt(options.infants, 10), options.babiesSingularLabel, options.babiesPluralLabel);
    const parts = [adultsLabel];
    if (childrenLabel) parts.push(childrenLabel);
    if (infantsLabel) parts.push(infantsLabel);
    return parts.join(", ");
}

export const prepareLabel = (count: number, textSingular: string, textPlural: string) => {
    let label = "";
    if(count) {
        label = `${count} ${count < 2 ? textSingular : textPlural}`;
    }
    return label;
}

export const validateEmail = (email: any) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
}

export function addToLSObject(lsObjName: string, attr: string, val: any) {
    const lsObj = JSON.parse((window as any).localStorage.getItem(lsObjName)) || {};

    lsObj[attr] = val;

    const userDataWI = JSON.stringify(lsObj);

    (window as any).localStorage.setItem(lsObjName, userDataWI)

}

/** Session storage helpers for booking flow (cleared when tab closes or after booking). */
export const BOOKING_SESSION_KEYS = {
    FLIGHTS: "pr_jet_flights_data",
    PASSENGERS: "pr_jet_passengers_data",
    SEAT_SELECTION: "pr_jet_seat_selection_data",
    SEAT_HOLD_OWNER: "pr_jet_seat_hold_owner_key",
    COUPON: "pr_jet_booking_coupon",
    PASSENGER_FORM: (index: number) => `passengerForm-${index}`,
} as const;

export function addToSessionObject(sessionObjName: string, attr: string, val: any) {
    try {
        const obj = JSON.parse((window as any).sessionStorage.getItem(sessionObjName) || "{}");
        obj[attr] = val;
        (window as any).sessionStorage.setItem(sessionObjName, JSON.stringify(obj));
    } catch (e) {
        console.warn("sessionStorage write failed", e);
    }
}

export function clearBookingSessionStorage() {
    try {
        (window as any).sessionStorage.removeItem(BOOKING_SESSION_KEYS.FLIGHTS);
        (window as any).sessionStorage.removeItem(BOOKING_SESSION_KEYS.PASSENGERS);
        (window as any).sessionStorage.removeItem(BOOKING_SESSION_KEYS.SEAT_SELECTION);
        (window as any).sessionStorage.removeItem(BOOKING_SESSION_KEYS.SEAT_HOLD_OWNER);
        (window as any).sessionStorage.removeItem(BOOKING_SESSION_KEYS.COUPON);
        const keysToRemove: string[] = [];
        for (let i = 0; i < (window as any).sessionStorage.length; i++) {
            const key = (window as any).sessionStorage.key(i);
            if (key && key.startsWith("passengerForm-")) keysToRemove.push(key);
        }
        keysToRemove.forEach((k) => (window as any).sessionStorage.removeItem(k));
    } catch (e) {
        console.warn("clearBookingSessionStorage failed", e);
    }
}

export const parseJwt = (token: string) => {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
};

const PENDING_CONFIRMATION_KEY = "pr_jet_pending_confirmation";

const PNR_KEYS = [
    "pnrCode",
    "pnr",
    "pnr_code",
    "PNR",
    "recordLocator",
    "bookingReference",
    "client_reference_id",
    "confirmationCode",
];
const LAST_NAME_KEYS = [
    "lastName",
    "last_name",
    "lastname",
    "surname",
    "customerLastName",
    "familyName",
];

const firstNonEmpty = (value: unknown): string => {
    if (typeof value === "string") return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value).trim();
    return "";
};

const findByKeys = (value: any, keys: string[], depth = 0): string => {
    if (value == null || depth > 6) return "";
    if (Array.isArray(value)) {
        for (const item of value) {
            const found = findByKeys(item, keys, depth + 1);
            if (found) return found;
        }
        return "";
    }
    if (typeof value !== "object") return "";

    const keySet = new Set(keys.map((key) => key.toLowerCase()));
    for (const [key, nested] of Object.entries(value)) {
        if (keySet.has(key.toLowerCase())) {
            const found = firstNonEmpty(nested);
            if (found) return found;
        }
    }
    for (const nested of Object.values(value)) {
        if (nested && typeof nested === "object") {
            const found = findByKeys(nested, keys, depth + 1);
            if (found) return found;
        }
    }
    return "";
};

export const unwrapApiData = (data: any): any => {
    if (data?.data && typeof data.data === "object" && !Array.isArray(data.data)) {
        return { ...data, ...data.data };
    }
    return data || {};
};

export const pickPnrCode = (data: any): string => {
    const found = findByKeys(data, PNR_KEYS);
    if (!found) return "";
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(found)) {
        return "";
    }
    return found;
};

export const pickLastName = (data: any): string => {
    const found = findByKeys(data, LAST_NAME_KEYS);
    if (found) return found;

    const travelerSurname = firstNonEmpty(
        data?.travelers?.[0]?.surname ||
            data?.reservation?.travelers?.[0]?.surname ||
            data?.data?.travelers?.[0]?.surname
    );
    if (travelerSurname) return travelerSurname;

    const fullName = firstNonEmpty(
        data?.customer_details?.name || data?.data?.customer_details?.name
    );
    if (fullName.includes(" ")) {
        return fullName.split(/\s+/).filter(Boolean).slice(-1)[0] || "";
    }
    return "";
};

export const pickReservationFromPayload = (data: any): any => {
    const candidates = [data, data?.reservation, data?.data, data?.data?.reservation];
    return (
        candidates.find(
            (item) => item && typeof item === "object" && Array.isArray(item.travelers)
        ) || null
    );
};

export const readPassengerLastName = (): string => {
    try {
        const raw = sessionStorage.getItem(BOOKING_SESSION_KEYS.PASSENGERS);
        if (raw) {
            const parsed = JSON.parse(raw);
            const list = Array.isArray(parsed?.passengers) ? parsed.passengers : [];
            const surname = firstNonEmpty(list[0]?.surname || list[0]?.lastName);
            if (surname) return surname;
        }
        const formRaw = sessionStorage.getItem(BOOKING_SESSION_KEYS.PASSENGER_FORM(0));
        if (formRaw) {
            const form = JSON.parse(formRaw);
            const surname = firstNonEmpty(form?.surname || form?.lastName);
            if (surname) return surname;
        }
    } catch {}
    return "";
};

export const rememberPendingConfirmation = (payload: {
    reservationId?: string;
    pnrCode?: string;
    lastName?: string;
}) => {
    const reservationId = String(payload.reservationId || "").trim();
    const pnrCode = String(payload.pnrCode || "").trim();
    const lastName = String(payload.lastName || "").trim();
    if (!reservationId && !pnrCode) return;

    try {
        localStorage.setItem(
            PENDING_CONFIRMATION_KEY,
            JSON.stringify({ reservationId, pnrCode, lastName })
        );
    } catch (e) {
        console.warn("rememberPendingConfirmation failed", e);
    }
};

export const readPendingConfirmation = (): {
    reservationId?: string;
    pnrCode?: string;
    lastName?: string;
} | null => {
    try {
        const raw = localStorage.getItem(PENDING_CONFIRMATION_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
        return null;
    }
};

export const clearPendingConfirmation = () => {
    try {
        localStorage.removeItem(PENDING_CONFIRMATION_KEY);
    } catch {}
};
